import { API_URL } from "./config";

const apiUrl = API_URL;

export interface ArticleSection {
  heading: string;
  body: string;
}

export interface Article {
  slug: string;
  title: string;
  category: string;
  date: string;
  read: string;
  image: string;
  excerpt: string;
  meta_title?: string;
  meta_description?: string;
  tags?: string[];
  updated_at?: string;
  sections: ArticleSection[];
}

export function optimizeImageUrl(img?: string, width = 800): string {
  if (!img) return "/og-share.png";
  if (img.includes("lh3.googleusercontent.com/d/")) {
    const base = img.split("=")[0];
    return `${base}=w${width}-rw`;
  }
  if (img.startsWith("http://") || img.startsWith("https://")) return img;
  if (img.startsWith("/articles/")) return img;
  if (img.startsWith("/static/")) return `${apiUrl}${img}`;
  return `${apiUrl}${img.startsWith("/") ? "" : "/"}${img}`;
}

export function formatArticleImage(img?: string): string {
  return optimizeImageUrl(img, 1000);
}

export function getShareImageUrl(img?: string): {
  url: string;
  type: string;
  width: number;
  height: number;
} {
  if (!img || img.endsWith(".svg")) {
    return {
      url: "https://buildwithjit.com/og-share.png",
      type: "image/png",
      width: 1200,
      height: 630,
    };
  }
  // Google Drive / lh3 usercontent images:
  if (img.includes("lh3.googleusercontent.com/d/")) {
    const base = img.split("=")[0];
    // IMPORTANT: Do NOT use -rw (which forces WebP)!
    // WhatsApp and LinkedIn crawlers reject WebP for link previews.
    // =w1200 guarantees a crisp JPEG (~250KB, within WhatsApp <300KB limit).
    return {
      url: `${base}=w1200`,
      type: "image/jpeg",
      width: 1200,
      height: 630,
    };
  }
  const fullUrl = img.startsWith("http")
    ? img
    : `https://buildwithjit.com${img.startsWith("/") ? "" : "/"}${img}`;
  const ext = (fullUrl.split("?")[0] ?? "").split(".").pop()?.toLowerCase();
  const type = ext === "png" ? "image/png" : "image/jpeg";
  return {
    url: fullUrl,
    type,
    width: 1200,
    height: 630,
  };
}

export async function fetchArticles(): Promise<Article[]> {
  try {
    const res = await fetch(`${apiUrl}/api/articles?_t=${Date.now()}`, {
      cache: "no-store",
      headers: { "Cache-Control": "no-cache" },
    });
    if (!res.ok) {
      console.warn(`Failed to fetch articles from ${apiUrl}/api/articles (status ${res.status})`);
      return [];
    }
    const data = await res.json();
    return data.map((article: Article) => ({
      ...article,
      image: formatArticleImage(article.image),
    }));
  } catch (error) {
    console.warn(`Could not connect to API at ${apiUrl}/api/articles during SSR:`, error);
    return [];
  }
}

export async function fetchSitemapArticles(): Promise<Article[]> {
  const res = await fetch(`${apiUrl}/api/sitemap-data`, { cache: "no-store" });
  if (!res.ok) {
    throw new Error(`Failed to fetch sitemap article data (status ${res.status})`);
  }
  const data = await res.json();
  return data.map((article: Article) => ({
    ...article,
    image: formatArticleImage(article.image),
  }));
}

export async function fetchArticle(slug: string): Promise<Article | null> {
  try {
    const res = await fetch(`${apiUrl}/api/articles/${slug}?_t=${Date.now()}`, {
      cache: "no-store",
      headers: { "Cache-Control": "no-cache" },
    });
    if (!res.ok) {
      return null;
    }
    const data = await res.json();
    return {
      ...data,
      image: formatArticleImage(data.image),
    };
  } catch (error) {
    console.warn(`Could not fetch article ${slug} from ${apiUrl}:`, error);
    return null;
  }
}
